import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { useState, useEffect } from "react";
import AstroMadlen from "@packages/assets/swann-icon.png";

type FavoriteButtonProps = {
  isFavorite: boolean;
  onToggleFavorite?: () => void;
};

export const FavoriteButton = ({
  isFavorite,
  onToggleFavorite,
}: FavoriteButtonProps) => {
  const [showAstroImage, setShowAstroImage] = useState(false);
  const [showStar, setShowStar] = useState(false);
  const [wasClicked, setWasClicked] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite);

  useEffect(() => {
    // Sadece kullanıcı tıkladığında ve favori olduğunda animasyonu başlat
    if (localIsFavorite && wasClicked) {
      setShowAstroImage(true);
      setShowStar(false);

      const timer = setTimeout(() => {
        setShowAstroImage(false);
        setShowStar(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (!localIsFavorite) {
      // Favori olmadığında animasyon durumlarını sıfırla
      setShowAstroImage(false);
      setShowStar(false);
      setWasClicked(false);
    } else if (localIsFavorite && !wasClicked) {
      // İlk render'da favoriyse ama tıklanmamışsa, doğrudan yıldızı göster
      setShowStar(true);
    }
  }, [localIsFavorite, wasClicked]);

  const handleClick = (e: React.MouseEvent) => {
    e?.stopPropagation();
    setLocalIsFavorite(!localIsFavorite);
    setWasClicked(true);
    if (onToggleFavorite) {
      onToggleFavorite();
    }
  };

  useEffect(() => {
    setLocalIsFavorite(isFavorite);
  }, [isFavorite]);

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        type="button"
        className={`flex items-center justify-center h-8 w-8 rounded-full
          cursor-pointer text-xs font-semibold outline-none transition-all duration-100 ease-out
          ${
            localIsFavorite
              ? "bg-yellow-100 text-yellow-600 dark:bg-dark-900 dark:text-yellow-500"
              : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-dark-900 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
      >
        <div className="relative w-4 h-4 flex items-center justify-center">
          {!localIsFavorite && <Star className="w-4 h-4 absolute inset-0" />}

          <AnimatePresence>
            {showStar && localIsFavorite && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0"
              >
                <Star className="w-4 h-4 fill-white text-gray-300 dark:text-gray-300" />
              </motion.div>
            )}
            {localIsFavorite && !showStar && !showAstroImage && (
              <Star className="w-4 h-4 fill-yellow-500 text-gray-300 dark:text-gray-300" />
            )}
          </AnimatePresence>
        </div>
      </button>

      <AnimatePresence>
        {showAstroImage && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={AstroMadlen}
              alt="Astro effect"
              className="w-12 h-12 z-10"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {localIsFavorite && wasClicked && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.2, 0], opacity: [0, 1, 0] }}
              transition={{ duration: 0.5 }}
              className="w-10 h-10 rounded-full bg-yellow-100 opacity-70"
            />
          </div>

          {[...Array(8)].map((_, i) => {
            const angle = (i * Math.PI) / 4;
            return (
              <motion.div
                key={i}
                className="absolute top-1/2 left-1/2 w-1 h-1 rounded-full bg-yellow-500"
                initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                animate={{
                  scale: [0, 1],
                  x: [0, Math.cos(angle) * 10],
                  y: [0, Math.sin(angle) * 10],
                  opacity: [0, 1, 0],
                }}
                transition={{ duration: 0.5 }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FavoriteButton;
